/**
 * useResident.ts — typed composables wrapping /v1/resident/* endpoints.
 *
 * Mirrors the cursor-pagination pattern from useIssues.ts.
 * Uses direct fetch (not openapi-fetch) because the resident endpoints are
 * not yet in the generated schema.d.ts (Implementer B lands those). Once B
 * merges and openapi:gen is re-run, swap to api.GET/POST.
 *
 * Each composable returns { data, isLoading, error, refetch }.
 * Paginated composables additionally expose { loadMore, hasMore }.
 */
import { ref, watch } from 'vue';
import { useAuth } from '@/stores/auth';
// ── Internal helper — auth-aware fetch ───────────────────────────────────────
async function residentFetch(path, options = {}) {
    const auth = useAuth();
    const token = await auth.getAccessToken();
    const headers = new Headers(options.headers ?? {});
    if (token)
        headers.set('Authorization', `Bearer ${token}`);
    headers.set('Content-Type', 'application/json');
    const res = await fetch(path, { ...options, headers });
    if (!res.ok) {
        const err = new Error(`${res.status} ${res.statusText}`);
        err.status = res.status;
        throw err;
    }
    return res.json();
}
// ── useResidentMe ─────────────────────────────────────────────────────────────
export function useResidentMe() {
    const data = ref(null);
    const isLoading = ref(false);
    const error = ref(null);
    async function fetch() {
        isLoading.value = true;
        error.value = null;
        try {
            data.value = await residentFetch('/v1/resident/me');
        }
        catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to load profile.';
        }
        finally {
            isLoading.value = false;
        }
    }
    void fetch();
    return { data, isLoading, error, refetch: fetch };
}
// ── useResidentIssues ─────────────────────────────────────────────────────────
export function useResidentIssues({ limit = 10 } = {}) {
    const items = ref([]);
    const nextCursor = ref(null);
    const isLoading = ref(false);
    const isLoadingMore = ref(false);
    const error = ref(null);
    async function fetchPage(cursor) {
        const isLoadMore = !!cursor;
        if (isLoadMore) {
            isLoadingMore.value = true;
        }
        else {
            isLoading.value = true;
            error.value = null;
        }
        try {
            const params = new URLSearchParams({ limit: String(limit) });
            if (cursor)
                params.set('cursor', cursor);
            const page = await residentFetch(`/v1/resident/issues?${params.toString()}`);
            if (isLoadMore) {
                items.value = [...items.value, ...page.items];
            }
            else {
                items.value = page.items;
            }
            nextCursor.value = page.nextCursor;
        }
        catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to load submissions.';
        }
        finally {
            isLoading.value = false;
            isLoadingMore.value = false;
        }
    }
    function loadMore() {
        if (!nextCursor.value || isLoadingMore.value)
            return;
        void fetchPage(nextCursor.value);
    }
    function refetch() {
        void fetchPage();
    }
    void fetchPage();
    return {
        items,
        nextCursor,
        hasMore: () => !!nextCursor.value,
        isLoading,
        isLoadingMore,
        error,
        loadMore,
        refetch,
    };
}
// ── useResidentIssue (single) ─────────────────────────────────────────────────
export function useResidentIssue(id) {
    const data = ref(null);
    const isLoading = ref(false);
    const error = ref(null);
    async function fetch() {
        if (!id.value)
            return;
        isLoading.value = true;
        error.value = null;
        try {
            data.value = await residentFetch(`/v1/resident/issues/${id.value}`);
        }
        catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to load submission.';
        }
        finally {
            isLoading.value = false;
        }
    }
    watch(id, () => void fetch(), { immediate: true });
    return { data, isLoading, error, refetch: fetch };
}
// ── useResidentAnnouncements ──────────────────────────────────────────────────
export function useResidentAnnouncements({ limit = 5 } = {}) {
    const items = ref([]);
    const nextCursor = ref(null);
    const isLoading = ref(false);
    const error = ref(null);
    async function fetchPage(cursor) {
        const isLoadMore = !!cursor;
        if (!isLoadMore) {
            isLoading.value = true;
            error.value = null;
        }
        try {
            const params = new URLSearchParams({ limit: String(limit) });
            if (cursor)
                params.set('cursor', cursor);
            const page = await residentFetch(`/v1/resident/announcements?${params.toString()}`);
            if (isLoadMore) {
                items.value = [...items.value, ...page.items];
            }
            else {
                items.value = page.items;
            }
            nextCursor.value = page.nextCursor;
        }
        catch (e) {
            error.value = e instanceof Error ? e.message : 'Failed to load announcements.';
        }
        finally {
            isLoading.value = false;
        }
    }
    void fetchPage();
    return { items, nextCursor, isLoading, error, refetch: () => void fetchPage() };
}
// ── registerResident (one-shot POST) ─────────────────────────────────────────
export async function registerResident(payload) {
    return residentFetch('/v1/resident/register', {
        method: 'POST',
        body: JSON.stringify(payload),
    });
}
